import type { SiteChatAuthor, SiteChatConfig, SiteChatField, SiteChatLang } from './types';
import { SITE_CHAT_FIELD_KINDS } from './types';
import { normalizeLinks } from './links';

type Phrase = {
    title: string;
    subtitleOnline: string;
    subtitleOffline: string;
    greeting: string;
    cardText: string;
    linksTitle: string;
    replyOnline: string;
    replyOffline: string;
    formTitle: string;
    button: string;
    empty: string;
    emailError: string;
    phoneError: string;
    network: string;
    composerPlaceholder: string;
    offlineBanner: string;
    noConnection: string;
    retry: string;
    /** «Rate the conversation» card after an operator closes it. */
    csatQuestion: string;
    name: string;
    email: string;
    phone: string;
    namePlaceholder: string;
    emailPlaceholder: string;
    phonePlaceholder: string;
};

const PHRASES: Record<SiteChatLang, Phrase> = {
    en: {
        title: 'Write to us',
        subtitleOnline: 'We usually reply within a few minutes',
        subtitleOffline: 'We are away right now',
        greeting: 'Hello! Send your question, we are here.',
        cardText: 'Send a message',
        linksTitle: 'Other ways to reach us',
        replyOnline: 'We are online and will reply shortly',
        replyOffline: 'We are away now. Leave a message and we will reply when we are back',
        formTitle: 'Introduce yourself',
        button: 'Continue',
        empty: 'Fill in this field',
        emailError: 'Email must contain @',
        phoneError: 'The number is incomplete',
        network: 'Could not send. Try again',
        composerPlaceholder: 'Message…',
        offlineBanner: 'We are away now. Leave a message and we will reply when we are back',
        noConnection: 'No connection. Check your internet',
        retry: 'Try again',
        csatQuestion: 'How would you rate the conversation?',
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        namePlaceholder: 'How should we address you',
        emailPlaceholder: 'name@mail.com',
        phonePlaceholder: '+1 555 000 0000',
    },
    ru: {
        title: 'Напишите нам',
        subtitleOnline: 'Обычно отвечаем в течение нескольких минут',
        subtitleOffline: 'Сейчас мы не в сети',
        greeting: 'Здравствуйте! Напишите вопрос, мы на связи',
        cardText: 'Написать сообщение',
        linksTitle: 'Другие способы связи',
        replyOnline: 'Мы на связи и скоро ответим',
        replyOffline: 'Сейчас мы не в сети. Оставьте сообщение, ответим, как только вернёмся',
        formTitle: 'Представьтесь',
        button: 'Продолжить',
        empty: 'Заполните поле',
        emailError: 'Email должен содержать @',
        phoneError: 'Номер введён не полностью',
        network: 'Не удалось отправить. Попробуйте ещё раз',
        composerPlaceholder: 'Сообщение…',
        offlineBanner: 'Сейчас мы не в сети. Оставьте сообщение, ответим, как только вернёмся',
        noConnection: 'Нет связи. Проверьте интернет',
        retry: 'Повторить',
        csatQuestion: 'Как вам наш разговор?',
        name: 'Имя',
        email: 'Email',
        phone: 'Телефон',
        namePlaceholder: 'Как к вам обращаться',
        emailPlaceholder: 'name@mail.ru',
        phonePlaceholder: '+7 900 000-00-00',
    },
    uk: {
        title: 'Напишіть нам',
        subtitleOnline: 'Зазвичай відповідаємо за кілька хвилин',
        subtitleOffline: 'Зараз ми не в мережі',
        greeting: 'Вітаємо! Напишіть питання, ми на зв’язку',
        cardText: 'Написати повідомлення',
        linksTitle: 'Інші способи зв’язку',
        replyOnline: 'Ми на зв’язку й скоро відповімо',
        replyOffline: 'Зараз ми не в мережі. Залиште повідомлення, відповімо, щойно повернемося',
        formTitle: 'Представтеся',
        button: 'Продовжити',
        empty: 'Заповніть поле',
        emailError: 'Email має містити @',
        phoneError: 'Номер введено не повністю',
        network: 'Не вдалося надіслати. Спробуйте ще раз',
        composerPlaceholder: 'Повідомлення…',
        offlineBanner: 'Зараз ми не в мережі. Залиште повідомлення, відповімо, щойно повернемося',
        noConnection: 'Немає зв’язку. Перевірте інтернет',
        retry: 'Повторити',
        csatQuestion: 'Як вам наша розмова?',
        name: 'Ім’я',
        email: 'Email',
        phone: 'Телефон',
        namePlaceholder: 'Як до вас звертатися',
        emailPlaceholder: 'name@mail.ua',
        phonePlaceholder: '+380 67 000 0000',
    },
    es: {
        title: 'Escríbenos',
        subtitleOnline: 'Solemos responder en unos minutos',
        subtitleOffline: 'Ahora no estamos en línea',
        greeting: 'Hola. Escribe tu pregunta, estamos aquí.',
        cardText: 'Enviar un mensaje',
        linksTitle: 'Otras formas de contactarnos',
        replyOnline: 'Estamos en línea y responderemos pronto',
        replyOffline: 'Ahora no estamos. Deja un mensaje y responderemos en cuanto volvamos',
        formTitle: 'Preséntate',
        button: 'Continuar',
        empty: 'Completa este campo',
        emailError: 'El email debe contener @',
        phoneError: 'El número está incompleto',
        network: 'No se pudo enviar. Inténtalo de nuevo',
        composerPlaceholder: 'Mensaje…',
        offlineBanner: 'Ahora no estamos. Deja un mensaje y responderemos en cuanto volvamos',
        noConnection: 'Sin conexión. Revisa tu internet',
        retry: 'Reintentar',
        csatQuestion: '¿Qué te pareció la conversación?',
        name: 'Nombre',
        email: 'Email',
        phone: 'Teléfono',
        namePlaceholder: 'Cómo debemos llamarte',
        emailPlaceholder: 'name@mail.com',
        phonePlaceholder: '+34 600 000 000',
    },
    id: {
        title: 'Tulis kepada kami',
        subtitleOnline: 'Kami biasanya membalas dalam beberapa menit',
        subtitleOffline: 'Kami sedang offline',
        greeting: 'Halo! Kirim pertanyaan Anda, kami siap membantu.',
        cardText: 'Kirim pesan',
        linksTitle: 'Cara lain menghubungi kami',
        replyOnline: 'Kami online dan akan segera membalas',
        replyOffline: 'Kami sedang offline. Tinggalkan pesan, kami akan membalas saat kembali',
        formTitle: 'Perkenalkan diri',
        button: 'Lanjut',
        empty: 'Isi bidang ini',
        emailError: 'Email harus berisi @',
        phoneError: 'Nomor belum lengkap',
        network: 'Gagal mengirim. Coba lagi',
        composerPlaceholder: 'Pesan…',
        offlineBanner: 'Kami sedang offline. Tinggalkan pesan, kami akan membalas saat kembali',
        noConnection: 'Tidak ada koneksi. Periksa internet Anda',
        retry: 'Coba lagi',
        csatQuestion: 'Bagaimana pendapat Anda tentang percakapan ini?',
        name: 'Nama',
        email: 'Email',
        phone: 'Telepon',
        namePlaceholder: 'Bagaimana kami menyapa Anda',
        emailPlaceholder: 'name@mail.com',
        phonePlaceholder: '+62 812 0000 0000',
    },
    lt: {
        title: 'Parašykite mums',
        subtitleOnline: 'Paprastai atsakome per kelias minutes',
        subtitleOffline: 'Šiuo metu neprisijungę',
        greeting: 'Sveiki! Parašykite klausimą, mes čia.',
        cardText: 'Rašyti žinutę',
        linksTitle: 'Kiti būdai susisiekti',
        replyOnline: 'Esame prisijungę ir netrukus atsakysime',
        replyOffline: 'Dabar esame neprisijungę. Palikite žinutę, atsakysime, kai grįšime',
        formTitle: 'Prisistatykite',
        button: 'Tęsti',
        empty: 'Užpildykite lauką',
        emailError: 'El. pašte turi būti @',
        phoneError: 'Numeris įvestas ne iki galo',
        network: 'Nepavyko išsiųsti. Bandykite dar kartą',
        composerPlaceholder: 'Žinutė…',
        offlineBanner: 'Dabar esame neprisijungę. Palikite žinutę, atsakysime, kai grįšime',
        noConnection: 'Nėra ryšio. Patikrinkite internetą',
        retry: 'Bandyti dar kartą',
        csatQuestion: 'Kaip vertinate mūsų pokalbį?',
        name: 'Vardas',
        email: 'El. paštas',
        phone: 'Telefonas',
        namePlaceholder: 'Kaip į jus kreiptis',
        emailPlaceholder: 'name@mail.com',
        phonePlaceholder: '+370 600 00000',
    },
    pt: {
        title: 'Escreva para nós',
        subtitleOnline: 'Normalmente respondemos em alguns minutos',
        subtitleOffline: 'Estamos offline agora',
        greeting: 'Olá! Envie a sua pergunta, estamos aqui.',
        cardText: 'Enviar uma mensagem',
        linksTitle: 'Outras formas de contato',
        replyOnline: 'Estamos online e respondemos em breve',
        replyOffline: 'Agora estamos offline. Deixe uma mensagem e respondemos quando voltarmos',
        formTitle: 'Apresente-se',
        button: 'Continuar',
        empty: 'Preencha este campo',
        emailError: 'O email deve conter @',
        phoneError: 'O número está incompleto',
        network: 'Não foi possível enviar. Tente de novo',
        composerPlaceholder: 'Mensagem…',
        offlineBanner: 'Agora estamos offline. Deixe uma mensagem e respondemos quando voltarmos',
        noConnection: 'Sem ligação. Verifique a internet',
        retry: 'Tentar de novo',
        csatQuestion: 'O que você achou da nossa conversa?',
        name: 'Nome',
        email: 'Email',
        phone: 'Telefone',
        namePlaceholder: 'Como devemos tratar-lhe',
        emailPlaceholder: 'name@mail.com',
        phonePlaceholder: '+351 900 000 000',
    },
    zhcn: {
        title: '给我们留言',
        subtitleOnline: '我们通常会在几分钟内回复',
        subtitleOffline: '我们现在不在线',
        greeting: '您好！请写下问题，我们在这里。',
        cardText: '发送消息',
        linksTitle: '其他联系方式',
        replyOnline: '我们在线，很快回复',
        replyOffline: '我们现在不在线。请留言，我们回来后会回复',
        formTitle: '请介绍一下自己',
        button: '继续',
        empty: '请填写此栏',
        emailError: '邮箱必须包含 @',
        phoneError: '号码不完整',
        network: '发送失败。请再试一次',
        composerPlaceholder: '消息…',
        offlineBanner: '我们现在不在线。请留言，我们回来后会回复',
        noConnection: '没有连接。请检查网络',
        retry: '重试',
        csatQuestion: '您觉得这次对话怎么样？',
        name: '姓名',
        email: '邮箱',
        phone: '电话',
        namePlaceholder: '我们如何称呼您',
        emailPlaceholder: 'name@mail.com',
        phonePlaceholder: '+86 138 0000 0000',
    },
    zhtw: {
        title: '寫信給我們',
        subtitleOnline: '我們通常會在幾分鐘內回覆',
        subtitleOffline: '我們現在不在線上',
        greeting: '您好！請寫下問題，我們在這裡。',
        cardText: '傳送訊息',
        linksTitle: '其他聯絡方式',
        replyOnline: '我們在線上，很快回覆',
        replyOffline: '我們現在不在線上。請留言，我們回來後會回覆',
        formTitle: '請介紹一下自己',
        button: '繼續',
        empty: '請填寫此欄',
        emailError: '電子郵件必須包含 @',
        phoneError: '號碼不完整',
        network: '傳送失敗。請再試一次',
        composerPlaceholder: '訊息…',
        offlineBanner: '我們現在不在線上。請留言，我們回來後會回覆',
        noConnection: '沒有連線。請檢查網路',
        retry: '重試',
        csatQuestion: '您覺得這次對話怎麼樣？',
        name: '姓名',
        email: '電子郵件',
        phone: '電話',
        namePlaceholder: '我們如何稱呼您',
        emailPlaceholder: 'name@mail.com',
        phonePlaceholder: '+886 912 345 678',
    },
};

function phrase(lang: SiteChatLang): Phrase {
    return PHRASES[lang] ?? PHRASES.en;
}

/**
 * What a new channel starts with. Nothing here may pretend to be the school's: no team, no policy link, no
 * working hours in the texts — the schedule is «always online» until the owner sets one.
 */
/** Server limits (`SiteMessageRestriction`): the owner can only go lower. */
export const SITE_CHAT_MAX_LENGTH = 10000;
export const SITE_CHAT_MAX_FILE_MB = 20;

function clampInt(value: unknown, min: number, max: number, fallback: number) {
    const number = Math.round(Number(value));
    return Number.isFinite(number) && number > 0 ? Math.min(max, Math.max(min, number)) : fallback;
}

/** A saved field as the widget can draw it: a contact binding, a known kind, list options as `{id, value}` only. */
function normalizeField(raw: SiteChatField): SiteChatField {
    const field = { ...raw };
    field.attr = typeof raw.attr === 'string' && raw.attr.trim() ? raw.attr.trim().slice(0, 40) : null;
    field.kind = raw.kind && SITE_CHAT_FIELD_KINDS.includes(raw.kind) ? raw.kind : null;
    field.options = Array.isArray(raw.options)
        ? raw.options
            .filter(option => option && typeof option.id === 'string' && typeof option.value === 'string')
            .slice(0, 100)
            .map(option => ({ id: option.id, value: option.value }))
        : null;
    // a list without options cannot be answered: asked as a text line
    if ((field.kind === 'list' || field.kind === 'multiList') && !field.options?.length)
        field.kind = null;
    return field;
}

export function normalizeExtension(value: unknown) {
    return String(value ?? '').trim().toLowerCase().replace(/^\.+/, '').replace(/[^a-z0-9]/g, '').slice(0, 10);
}

export function defaultSiteChatConfig(lang: SiteChatLang = 'en'): SiteChatConfig {
    const p = phrase(lang);
    return {
        colors: {
            button: '#1677ff',
            header: '#1677ff',
            guestBubble: '#1677ff',
            guestText: '#ffffff',
            operatorBubble: '#f0f2f5',
            operatorText: 'rgba(0,0,0,.88)',
        },
        theme: 'light',
        animations: true,
        emoji: true,
        voice: true,
        readReceipts: false,
        maxLength: SITE_CHAT_MAX_LENGTH,
        maxFileSizeMb: SITE_CHAT_MAX_FILE_MB,
        allowedExtensions: [],
        position: 'right',
        offsetX: 20,
        offsetY: 20,
        width: 380,
        lang,
        logo: null,
        title: p.title,
        subtitleOnline: p.subtitleOnline,
        subtitleOffline: p.subtitleOffline,
        greeting: p.greeting,
        cardText: p.cardText,
        skipHome: false,
        links: [],
        linksTitle: p.linksTitle,
        policyUrl: '',
        showTeam: false,
        showReplyTime: true,
        replyOnline: p.replyOnline,
        replyOffline: p.replyOffline,
        formEnabled: true,
        formRequiredOnline: false,
        formRequiredOffline: true,
        fields: [
            { name: p.name, type: 'text', required: true, enabled: true, builtin: true, placeholder: p.namePlaceholder },
            { name: p.email, type: 'email', required: true, enabled: true, builtin: true, placeholder: p.emailPlaceholder },
            // In the list, switched off (owner 2026-10-24): name and email only by default; the owner turns it on in the
            // settings. Kept third so the field indexes of saved answers stay where they were.
            { name: p.phone, type: 'phone', required: false, enabled: false, builtin: true, placeholder: p.phonePlaceholder },
        ],
        formPhrases: {
            title: p.formTitle,
            button: p.button,
            empty: p.empty,
            email: p.emailError,
            phone: p.phoneError,
            network: p.network,
        },
        composerPlaceholder: p.composerPlaceholder,
        offlineBanner: p.offlineBanner,
        noConnection: p.noConnection,
        retry: p.retry,
        csatQuestion: p.csatQuestion,
        alwaysOnline: true,
        timezone: 'Europe/Moscow',
        days: [0, 1, 2, 3, 4, 5, 6].map(index => ({
            on: index < 5,
            from: '10:00',
            to: '19:00',
        })),
        mobileOnly: false,
        hideMobile: false,
        rules: [],
        triggers: [],
    };
}

/**
 * Fills whatever a stored config lacks (an older widget, a hand-edited row) from the defaults of its language,
 * so the widget and the admin form never read `undefined` off a half-saved config.
 */
export function normalizeSiteChatConfig(raw: Partial<SiteChatConfig> | null | undefined, fallbackLang: SiteChatLang = 'en'): SiteChatConfig {
    const lang = raw?.lang && PHRASES[raw.lang] ? raw.lang : fallbackLang;
    const base = defaultSiteChatConfig(lang);
    if (!raw || typeof raw !== 'object')
        return base;
    return {
        ...base,
        ...raw,
        lang,
        theme: raw.theme === 'dark' || raw.theme === 'system' ? raw.theme : 'light',
        formEnabled: raw.formEnabled !== false,
        animations: raw.animations !== false,
        emoji: raw.emoji !== false,
        voice: raw.voice !== false,
        readReceipts: raw.readReceipts === true,
        skipHome: raw.skipHome === true,
        links: normalizeLinks(raw.links),
        maxLength: clampInt(raw.maxLength, 1, SITE_CHAT_MAX_LENGTH, SITE_CHAT_MAX_LENGTH),
        maxFileSizeMb: clampInt(raw.maxFileSizeMb, 1, SITE_CHAT_MAX_FILE_MB, SITE_CHAT_MAX_FILE_MB),
        allowedExtensions: Array.isArray(raw.allowedExtensions)
            ? [...new Set(raw.allowedExtensions.map(normalizeExtension).filter(Boolean))].slice(0, 50)
            : [],
        colors: { ...base.colors, ...(raw.colors ?? {}) },
        formPhrases: { ...base.formPhrases, ...(raw.formPhrases ?? {}) },
        fields: Array.isArray(raw.fields) ? raw.fields.slice(0, 8).map(normalizeField) : base.fields,
        days: Array.isArray(raw.days) && raw.days.length === 7 ? raw.days : base.days,
        rules: Array.isArray(raw.rules) ? raw.rules : [],
        triggers: Array.isArray(raw.triggers) ? raw.triggers : [],
        // saved before the card existed, or cleared: the language's own question
        csatQuestion: typeof raw.csatQuestion === 'string' && raw.csatQuestion.trim() ? raw.csatQuestion : base.csatQuestion,
    };
}

const TEXT_KEYS = ['title', 'subtitleOnline', 'subtitleOffline', 'greeting', 'cardText', 'linksTitle', 'replyOnline', 'replyOffline',
    'composerPlaceholder', 'offlineBanner', 'noConnection', 'retry', 'csatQuestion'] as const;

/**
 * Switches the widget language without losing the owner's work: a text still equal to the old language's
 * default follows the new language, a text the owner changed stays as written. Colors, hours, rules and the
 * rest of the config are untouched.
 */
export function switchSiteChatLanguage(config: SiteChatConfig, lang: SiteChatLang): SiteChatConfig {
    const from = defaultSiteChatConfig(config.lang);
    const to = defaultSiteChatConfig(lang);
    const next: SiteChatConfig = { ...config, lang, formPhrases: { ...config.formPhrases } };
    for (const key of TEXT_KEYS) {
        if (config[key] === from[key])
            next[key] = to[key];
    }
    for (const key of Object.keys(to.formPhrases) as Array<keyof SiteChatConfig['formPhrases']>) {
        if (config.formPhrases[key] === from.formPhrases[key])
            next.formPhrases[key] = to.formPhrases[key];
    }
    next.fields = config.fields.map(field => {
        const fromField = from.fields.find(item => item.builtin && item.type === field.type);
        const toField = to.fields.find(item => item.builtin && item.type === field.type);
        if (!field.builtin || !fromField || !toField)
            return field;
        return {
            ...field,
            name: field.name === fromField.name ? toField.name : field.name,
            placeholder: field.placeholder === fromField.placeholder ? toField.placeholder : field.placeholder,
        };
    });
    return next;
}

/** The team of the design's frames, used by the state gallery. */
export function designMockTeam(): SiteChatAuthor[] {
    return [{ name: 'Анна' }, { name: 'Иван' }, { name: 'Мария' }];
}

/** Sample copy from the design, used by the state gallery. Never a default for a real channel. */
export function designMockConfig(): SiteChatConfig {
    const config = defaultSiteChatConfig('ru');
    config.policyUrl = 'https://example.com/privacy';
    config.showTeam = true;
    config.replyOnline = 'Отвечаем сегодня до 19:00';
    config.replyOffline = 'Напишите, ответим завтра с 10:00';
    config.offlineBanner = 'Сейчас нерабочее время. Напишите, ответим завтра с 10:00';
    config.formRequiredOnline = true;
    config.fields = [
        ...config.fields,
        { name: 'Компания', type: 'text', required: false, enabled: true, builtin: false, placeholder: 'Название компании' },
    ];
    return config;
}
